import { kebabCase } from 'lodash';

type Category = {
  id: string;
  name: string;
  alias?: string;
  subcategories: Array<{
    eligible: boolean;
    value: string;
  }>;
};

type Vendor = {
  categories: Array<string>;
  id: string;
  is_checkout_enabled: boolean;
  name: string;
};

export type VendorListing = {
  id: string;
  key: string;
  title: string;
};

type VendorProduct = {
  categories: Array<string>;
  id: string;
  special_price: number;
  title: string;
};

type VendorVenue = {
  address: {
    address_line1: string;
    city: string;
    zip: string;
  };
  title: string;
};

export const initiateProductCheckout = ({
  position,
  product,
  vendor,
  vendor_venue,
}: {
  position?: string;
  product: VendorProduct;
  vendor: Vendor;
  vendor_venue?: VendorVenue;
}) => {
  let checkout_url = `/checkout?type=store&vid=${vendor.id}&pid=${product.id}&ref=product`;

  if (vendor_venue) {
    const { address } = vendor_venue;
    checkout_url += `&selected_vendor_venue_location=${vendor_venue.title}, ${address.address_line1}, ${address.city} - ${address.zip}`;
  }

  const initial_attributes = {
    vendor_id: vendor.id,
    vendor_name: vendor.name,
    vendor_categories: vendor.categories,
    product_id: product.id,
    name: product.title,
    categories: product.categories,
    price: product.special_price,
  };
  // if position is set, add it to the attirutes sent to amplitude
  const attributes = position
    ? { ...initial_attributes, ...{ position } }
    : initial_attributes;

  return { attributes, checkout_url };
};

export const getPriceDifference = ({
  msrp,
  sale_price,
}: {
  msrp: number | { value: number };
  sale_price: number | { value: number };
}) => {
  // TODO: remove this crazy type-checking when we stop using points (twitch only for now)
  const msrp_value = typeof msrp === 'object' ? (msrp.value as number) : msrp;
  const sale_value =
    typeof sale_price === 'object' ? (sale_price.value as number) : sale_price;

  const price_difference = msrp_value - sale_value;
  const percent_saved = ((price_difference / msrp_value) * 100).toFixed();

  return {
    price_difference,
    percent_saved,
  };
};

export const mapCategoriesToSubcategories = (categories: Array<Category>) =>
  categories.reduce((acc: Array<string>, current) => {
    const { subcategories } = current;
    const transaformed_subcategories = subcategories.reduce(
      (subs: Array<string>, current_sub) => {
        const { eligible, value } = current_sub;
        return eligible ? [...subs, value] : subs;
      },
      []
    );
    return [...acc, ...transaformed_subcategories];
  }, []);

export const extractVendorsFromProducts = <
  T extends {
    vendor_id: string;
    vendor_title: string;
  }
>(
  products: Array<T> = []
) => {
  const vendors: {
    [key: string]: Array<VendorListing>;
  } = {};

  products.forEach(({ vendor_id, vendor_title }) => {
    const key = kebabCase(vendor_title);
    const info = { id: vendor_id, key, title: vendor_title };
    if (vendors[key]) {
      vendors[key].push(info);
    } else {
      vendors[key] = [info];
    }
  });

  const sortedVendors: {
    [key: string]: Array<VendorListing>;
  } = {};

  Object.keys(vendors)
    .sort()
    .forEach((vendorSlug: string) => {
      sortedVendors[vendorSlug] = vendors[vendorSlug];
    });

  return sortedVendors;
};
